import {
	Chart,
	CategoryScale,
	LinearScale,
	LogarithmicScale,
	PointElement,
	LineElement,
	LineController,
	ScatterController,
	Tooltip,
	Legend,
	Filler,
} from 'chart.js';

Chart.register(
	CategoryScale,
	LinearScale,
	LogarithmicScale,
	PointElement,
	LineElement,
	LineController,
	ScatterController,
	Tooltip,
	Legend,
	Filler,
);

/* Exposed in dev only, so chart state can be inspected from the console
 * and from scripts/shoot.mjs. Stripped from production builds. */
if (import.meta.env.DEV) {
	window.Chart = Chart;
}

/*
 * Canvas cannot read CSS custom properties, so token values are resolved
 * against the document at render time. Reading them here (rather than
 * hardcoding, as directives.js:77 did) keeps charts in step with the theme,
 * including dark mode.
 */
export function readToken(name, fallback = '#888') {
	if (typeof window === 'undefined') return fallback;
	const value = getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim();
	return value || fallback;
}

export function seriesColors() {
	return [1, 2, 3, 4, 5, 6, 7].map((i) => readToken(`--chart-${i}`));
}

export function baseOptions() {
	const grid = readToken('--chart-grid', '#e9ecef');
	const axisText = readToken('--chart-axis-text', '#868e96');
	const tooltipBg = readToken('--chart-tooltip-bg', '#212529');
	const tooltipFg = readToken('--chart-tooltip-fg', '#ffffff');

	const reducedMotion =
		typeof window !== 'undefined' &&
		window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

	return {
		responsive: true,
		maintainAspectRatio: false,
		/* The default 1s progressive line animation makes a seven-chart page
		 * feel sluggish, and a half-drawn line reads as missing data. */
		animation: reducedMotion ? false : { duration: 300 },
		interaction: { mode: 'index', intersect: false },
		plugins: {
			legend: {
				position: 'bottom',
				labels: {
					color: axisText,
					usePointStyle: true,
					pointStyle: 'line',
					boxWidth: 24,
					padding: 16,
				},
			},
			tooltip: {
				backgroundColor: tooltipBg,
				titleColor: tooltipFg,
				bodyColor: tooltipFg,
				padding: 10,
				cornerRadius: 6,
				displayColors: true,
			},
		},
		scales: {
			x: {
				grid: { color: grid, drawTicks: false },
				border: { display: false },
				ticks: { color: axisText, maxRotation: 0, autoSkipPadding: 12 },
			},
			y: {
				grid: { color: grid, drawTicks: false },
				border: { display: false },
				ticks: { color: axisText },
			},
		},
	};
}

/* Rank axes span 1 to 10M; plain tick labels are unreadable at that range. */
export function compactRankTick(value) {
	const allowed = [1, 10, 100, 1000, 10000, 100000, 1000000, 10000000];
	if (!allowed.includes(value)) return null;
	if (value >= 1_000_000) return `${value / 1_000_000}M`;
	if (value >= 1_000) return `${value / 1_000}K`;
	return String(value);
}
